import { motion } from "framer-motion"
import { StaticImage } from "gatsby-plugin-image"
import { useEffect, useRef, useState } from "react"
import Gitex_logo from "../../images/client-logos/latest/gitex.png"
import Gitex_poster from "../../images/gitex-video-poster.png"
import AnnounceIcon from "../../images/icons/new/announce.png"
import CashIcon from "../../images/icons/new/cash.png"
import GraphIcon from "../../images/icons/new/graph.png"
import ThumbIcon from "../../images/icons/new/thumb.png"
import AnimatedText from "../../myne/AnimatedText/AnimatedText"
import { Button } from "../../myne/Button/Button"

import NumberCounter from "../NumberCounter"
import { VideoPlayer } from "../VideoPlayer"

const StatsData = [
  {
    val: "32K+",
    label: `Activated <br /> Attendees`,
    icon: <img src={ThumbIcon} width={30} alt="Announce icon" />,
  },
  {
    val: "3.7",
    label: "Referrals <br/> Per Advocate",
    icon: <img src={AnnounceIcon} width={30} alt="Attendee icon" />,
  },
  {
    val: "5.97M+",
    label: "Potential <br/> Reach",
    icon: <img src={GraphIcon} width={30} alt="Globe icon" />,
  },
  {
    val: "$160K+",
    label: "Ad Spend <br/> Saved",
    icon: <img src={CashIcon} width={30} alt="Star icon" />,
  },
]

const items = [
  "32K+ Activated Attendees",
  "5.97M+ Potential Reach",
  "$160K+ Ad Spend Saved",
]

const HighlightCaseStudy = () => {
  const [active, setActive] = useState(0)
  const [inView, setInView] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % items.length)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  const getScale = i => {
    if (i === active) return 1.3
    if (
      i === active - 1 ||
      i === active + 1 ||
      (active === 0 && i === 2) ||
      (active === 2 && i === 0)
    )
      return 0.7
    return 1
  }

  return (
    <section ref={sectionRef} className="w-full pt-20 md:pt-12 pb-12 md:pb-24 px-6 relative z-40">
      <div className="absolute bottom-0 right-0 -z-[1]">
        <StaticImage src="../../images/ellipse-bg.png" alt="Ellipse" />
      </div>
      <div className="text-center max-w-full md:max-w-5xl mx-auto">
        <h2 className="text-[30px] md:text-6xl font-semibold text-gray-900 !leading-[1.2em] text-left md:text-center">
          GITEX GLOBAL Leveraged
          <br />
          Attendee-Led Marketing to Drive{" "}
          <AnimatedText>200K+ Visitors</AnimatedText>
        </h2>
      </div>

      <div className="lg:max-w-7xl md:max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-2 lg:gap-24 gap-12 mt-16 relative z-40">
        {StatsData.map((stat, idx) => {
          return (
            <div
              key={idx}
              className="flex md:flex-col md:items-start items-center md:items-start"
            >
              <div className="w-12 h-12 p-2 md:p-0 md:w-16 md:h-16 rounded-full border border-[#EFEBFF] flex items-center justify-center md:mb-6 mr-6 md:mr-0">
                {stat.icon}
              </div>

              <div className="flex items-center">
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight w-[120px] md:w-auto flex items-center">
                  <NumberCounter value={stat.val} duration={2} />
                </h3>

                <p
                  className="text-gray-600 text-base md:text-base leading-snug whitespace-pre-line text-left md:ml-3 ml-4"
                  dangerouslySetInnerHTML={{ __html: stat.label }}
                ></p>
              </div>
            </div>
          )
        })}
      </div>
      <div className="max-w-7xl mx-auto gap-4 flex flex-col md:flex-row w-full mt-20">
        <div className="rounded-3xl overflow-hidden w-full h-full">
          <VideoPlayer
            type="controls"
            url="https://asts.premagic.com/v/gitex-video.mp4"
            muted
            thumbnail={inView ? Gitex_poster : undefined}
            autoPlay
          />
        </div>

        <div
          className="w-full md:w-[850px] rounded-3xl flex items-center flex-col justify-between md:p-16 p-10 pb-4 md:pb-16 
             bg-gradient-to-br from-[#EFEBFF]/70 to-[#fff]/60 border border-[#EFEBFF] backdrop-blur-sm shadow-sm"
        >
          <img
            src={Gitex_logo}
            alt="Gitex Logo"
            className="w-24 md:w-32 mx-auto mb-6"
          />
          <div className="text-center mb-6 md:mb-0">
            {items.map((text, i) => (
              <motion.p
                key={i}
                animate={{
                  scale: getScale(i),
                  opacity: i === active ? 1 : 0.6,
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="text-xl md:text-3xl font-bold text-gray-900 mb-2"
              >
                {text}
              </motion.p>
            ))}
          </div>
          <Button to="/case-study/gitex-global-2025/" variant="tertiary">
            View Case Study
          </Button>
        </div>
      </div>
    </section>
  )
}

export default HighlightCaseStudy
